import { useCallback, useState } from 'react';
import Toolbar from 'src/components/Toolbar';
import { useTranslation } from 'react-i18next';
import InfoItem from 'src/components/InfoItem';
import Button, { ButtonTypes } from 'src/components/Button';
import TextInput from 'src/components/TextInput';
import TextArea from 'src/components/TextArea';
import { useHistory } from 'react-router-dom';
import infoIcon from 'src/assets/info.svg';
import './style.scoped.css';
import forwardIcon from 'src/assets/forward_white.svg';
import UserInfoService from 'src/utils/UserInfoService';
import cancerStore, { CancerStore } from 'src/stores/CancerStore';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  isValidEmail,
  confirmQuitFlow,
  censorGTTT,
  censorPhoneNumber,
} from 'src/utils';
import { gaTrackEvent } from '../../api';
import { BUYING_PROCESS, BUYING_PROCESS_STEP, BUYING_PROCESS_TRACKING } from '../../constants';
dayjs.extend(customParseFormat);

interface ConfirmInfoObserverProps {
  cancerStore?: CancerStore;
}
const ConfirmInfoObserver = observer(
  ({ cancerStore }: ConfirmInfoObserverProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [email, setEmail] = useState(
      cancerStore.formInfo?.email || UserInfoService.getUserInfo()?.email || ''
    );
    const [address, setAddress] = useState(
      cancerStore.formInfo?.address ||
        UserInfoService.getUserInfo()?.address ||
        ''
    );
    const [errAddress, setErrAddress] = useState();
    const [errEmail, setErrEmail] = useState();

    const _handleContinue = useCallback(() => {
      console.log('Continue ConfirmInfo', address);
      let isError = false;
      if (!email || !email.trim()) {
        setErrEmail(t('email_required'));
        isError = true;
      } else if (!isValidEmail(email)) {
        setErrEmail(t('invalid_email'));
        isError = true;
      }
      if (!address || !address.trim()) {
        setErrAddress(t('address_required'));
        isError = true;
      }
      if (isError) return;

      gaTrackEvent(BUYING_PROCESS_TRACKING,
        BUYING_PROCESS[BUYING_PROCESS_STEP.ConfirmContactInfo].name,
        UserInfoService.getUserInfo()?.idCardNo ?? "");

      cancerStore.confirmInfo(email, address, history);
    }, [email, address, history, t, cancerStore]);

    const _handleChangeAddress = useCallback((event) => {
      setAddress(event.target.value.slice(0, 100));
      setErrAddress(null);
    }, []);

    const _handleChangeEmail = useCallback((event) => {
      setEmail(event.target.value.slice(0, 100));
      setErrEmail(null);
    }, []);

    const _handleClose = () => {
      confirmQuitFlow(history);
    };

    const _handleBack = () => {
      history.goBack();
      cancerStore.updateFormInfo({ email: null, address: null });
    };

    return (
      <div className="screen-container bg-background">
        <Toolbar
          title={t('confirm_info')}
          type="normal"
          showClose={true}
          onClose={_handleClose}
          onBack={_handleBack}
        />
        <div className="screenScrollContainer">
          <div className="hintContainer">
            <img src={infoIcon} className="w-8 h-8 mr-4" />
            <div className="hintText">{t('confirm_info_hint')}</div>
          </div>
          <div className="p-4 text-sm font-normal text-error italic">
            {t('require_hint')}
          </div>
          <div className="bg-surface pt-6 pb-7">
            {/* <div
            className={
              "text-sm text-secondary font-medium pb-4 border-b border-onSurface10 mb-4"
            }
          >
            {t("require_hint")}
          </div> */}
            <div className="mb-6 px-5">
              <InfoItem
                title={t('full_name_insurance')}
                content={UserInfoService.getUserInfo()?.fullname || ''}
              />
            </div>
            {!!UserInfoService.getUserInfo()?.idCardType && (
              <div className="mb-6 px-5">
                <InfoItem
                  title={t('identity_card_type')}
                  content={UserInfoService.getUserInfo()?.idCardType || ''}
                />
              </div>
            )}
            <div className="mb-6 px-5">
              <InfoItem
                title={t('identity_card_number')}
                content={
                  UserInfoService.getUserInfo()?.idCardNo
                    ? censorGTTT(UserInfoService.getUserInfo()?.idCardNo)
                    : ''
                }
              />
            </div>

            {!!UserInfoService.getUserInfo()?.issueDate && (
              <div className="mb-6 px-5">
                <InfoItem
                  title={t('issued_date')}
                  content={UserInfoService.getUserInfo()?.issueDate || ''}
                />
              </div>
            )}

            {!!UserInfoService.getUserInfo()?.issuePlace && (
              <div className="mb-6 px-5">
                <InfoItem
                  title={t('issued_place')}
                  content={UserInfoService.getUserInfo()?.issuePlace || ''}
                />
              </div>
            )}

            <div className="mb-6 px-5">
              <InfoItem
                title={t('nationality')}
                content={
                  cancerStore.nationalitys?.find(
                    (item) => item.id == cancerStore.formInfo?.nationality
                  )?.name || ''
                }
              />
            </div>
            <div className="mb-6 px-5">
              <InfoItem
                title={t('dob')}
                content={
                  cancerStore?.formInfo?.birthDate
                    ? dayjs(cancerStore?.formInfo?.birthDate).format(
                        t('date_format1')
                      )
                    : ''
                }
              />
            </div>
            <div className="mb-6 px-5">
              <InfoItem
                title={t('gender')}
                content={
                  cancerStore?.formInfo?.gender
                    ? t(cancerStore?.formInfo?.gender?.toLocaleLowerCase())
                    : ''
                }
              />
            </div>

            <div className="h-1 bg-line" />
            <div className="mb-6 px-5 mt-6">
              <TextInput
                label={t('email')}
                value={email}
                onChange={_handleChangeEmail}
                inputMode={'email'}
                helperText={t('confirm_info_email_hint')}
                isRequired={true}
                maxLength={100}
                placeholder={t('email_placeholder')}
              />
              {!!errEmail && (
                <div className={'helperTextError'}>{errEmail}</div>
              )}
            </div>
            <div className="mb-6 px-5">
              <TextArea
                label={t('address')}
                value={address}
                onChange={_handleChangeAddress}
                isRequired={true}
                error={!!errAddress}
                helperText={errAddress}
                maxLength={100}
                placeholder={t('address_placeholder')}
              />
            </div>
            <div className="h-1 bg-line" />
            <div className="mt-6 px-5">
              <InfoItem
                title={t('phone')}
                content={
                  UserInfoService.getUserInfo()?.mobile
                    ? censorPhoneNumber(UserInfoService.getUserInfo()?.mobile)
                    : ''
                }
              />
            </div>
          </div>
        </div>
        <div className="bottomButtonBlock">
          <div className="flex flex-row">
            <Button
              text={t('continue')}
              buttonType={ButtonTypes.primaryFilled}
              extraClassName="w-full"
              onClick={_handleContinue}
              rightIcon={<img src={forwardIcon} className="w-6 h-6 ml-2" />}
            />
          </div>
        </div>
      </div>
    );
  }
);

const ConfirmInfo = () => <ConfirmInfoObserver cancerStore={cancerStore} />;
export default ConfirmInfo;
